import {FieldInterface} from "@/types/Field.interface";

export interface FormFieldsApi<F extends Record<string, FieldInterface>> {
    getKeyValue(): { [K in keyof F]: F[K]["value"] };

    setKey<
        K extends keyof FieldInterface
    >(
        field: K,
        valueOrMap: FieldInterface[K] |
            Partial<{ [P in keyof F]: FieldInterface[K] }>
    ): FormFieldsApi<F>;

    build(): F;
}

export function FormFields<F extends Record<string, FieldInterface>>(
    initialFields: F
): FormFieldsApi<F> {
    const keys = Object.keys(initialFields) as (keyof F)[];
    let currentFields: F = {...initialFields};

    const api: FormFieldsApi<F> = {
        getKeyValue() {
            return Object.fromEntries(
                keys.map(k => [k, currentFields[k].value])
            ) as any;
        },

        setKey(field, valueOrMap) {
            const isMap = valueOrMap !== null &&
                typeof valueOrMap === "object" &&
                !Array.isArray(valueOrMap);

            const updated = {} as F;
            for (const k of keys) {
                const old = currentFields[k];
                const next = isMap
                    ? (valueOrMap as Record<keyof F, any>)[k] ?? old[field]
                    : valueOrMap;
                updated[k] = {...old, [field]: next};
            }

            currentFields = updated;
            return api;
        },

        build() {
            const snapshot = {} as F;
            for (const k of keys) {
                snapshot[k] = {...currentFields[k]};
            }
            return snapshot;
        }
    };

    return api;
}
